import React, { useEffect, useState, useCallback } from 'react';
import {
  Card, Table, DatePicker, Select, Input, Tag, Modal, Spin, Descriptions,
  Typography, Row, Col, Button, Statistic, message as antMessage,
} from 'antd';
import {
  ReloadOutlined, ScheduleOutlined, ClockCircleOutlined, TeamOutlined,
  CheckCircleTwoTone, CloseCircleTwoTone,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { fetchConvocations, fetchConvocationDetail } from '../services/convocationsApi';

const { RangePicker } = DatePicker;
const { Title, Text } = Typography;

// Mapeamento dos nomes internos para a numeração que o organizador vê na
// tela de Jogos: early = 1ª, first = 2ª, second = 3ª, third = 4ª
// (definido pelo form de Customers.js, label "Nª Convocação").
const CALL_TYPE_OPTIONS = [
  { value: 'early',  label: '1ª', color: 'default' },
  { value: 'first',  label: '2ª', color: 'blue' },
  { value: 'second', label: '3ª', color: 'orange' },
  { value: 'third',  label: '4ª', color: 'red' },
];

const STATUS_OPTIONS = [
  { value: 'pending', label: 'Pendente', color: 'gold' },
  { value: 'sent',    label: 'Enviada',  color: 'green' },
];

const callTypeMeta = (t) =>
  CALL_TYPE_OPTIONS.find((o) => o.value === t) || { label: t, color: 'default' };

const Convocations = () => {
  const [range, setRange] = useState([dayjs(), dayjs()]);
  const [callType, setCallType] = useState(null);
  const [status, setStatus] = useState(null);

  const [customerNameInput, setCustomerNameInput] = useState('');
  const [customerName, setCustomerName] = useState('');

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [total, setTotal] = useState(0);
  const [pendingCount, setPendingCount] = useState(0);
  const [teamsCount, setTeamsCount] = useState(0);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const [detailOpen, setDetailOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const openDetail = async (gameId) => {
    setDetailOpen(true);
    setDetail(null);
    setDetailLoading(true);
    try {
      const data = await fetchConvocationDetail(gameId);
      setDetail(data);
    } catch (e) {
      antMessage.error('Erro ao carregar detalhes');
    } finally {
      setDetailLoading(false);
    }
  };

  useEffect(() => {
    const t = setTimeout(() => {
      setCustomerName(customerNameInput.trim());
      setPage(1);
    }, 400);
    return () => clearTimeout(t);
  }, [customerNameInput]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        date_from: range[0].format('YYYY-MM-DD'),
        date_to: range[1].format('YYYY-MM-DD'),
        page,
        page_size: pageSize,
      };
      if (customerName) params.customer_name = customerName;
      if (callType)     params.call_type = callType;
      if (status)       params.status = status;

      const data = await fetchConvocations(params);
      setItems(data.items);
      setTotal(data.total);
      setPendingCount(data.pending_count ?? 0);
      setTeamsCount(data.teams_count ?? 0);
    } catch (e) {
      antMessage.error('Erro ao carregar convocações');
    } finally {
      setLoading(false);
    }
  }, [range, customerName, callType, status, page, pageSize]);

  useEffect(() => { load(); }, [load]);

  const columns = [
    {
      title: 'Hora prevista',
      dataIndex: 'call_at',
      width: 130,
      render: (v) => v ? dayjs(v).format('DD/MM HH:mm') : '-',
    },
    { title: 'Time', dataIndex: 'customer_name', width: 200 },
    {
      title: 'Dia do jogo',
      dataIndex: 'date_of_game',
      width: 130,
      render: (v) => v ? dayjs(v).format('DD/MM/YYYY') : '-',
    },
    {
      title: 'Tipo',
      dataIndex: 'call_type',
      width: 100,
      render: (t) => {
        const meta = callTypeMeta(t);
        return <Tag color={meta.color}>{meta.label}</Tag>;
      },
    },
    {
      title: 'Status',
      dataIndex: 'sent',
      width: 120,
      render: (sent) => sent
        ? <Tag color="green">Enviada</Tag>
        : <Tag color="gold" icon={<ClockCircleOutlined />}>Pendente</Tag>,
    },
    { title: 'Jogadores',   dataIndex: 'expected_players', width: 100, align: 'right' },
    {
      title: 'Enviados',
      dataIndex: 'sent_count',
      width: 100,
      align: 'right',
      render: (n, row) => {
        const isComplete = row.expected_players > 0 && n >= row.expected_players;
        return <span style={{ color: isComplete ? '#52C41A' : undefined }}>{n}</span>;
      },
    },
    { title: 'Confirmados', dataIndex: 'confirmed_count', width: 110, align: 'right' },
  ];

  return (
    <div>
      <Title level={3}>Convocações</Title>

      <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
        <Col xs={24} sm={8}>
          <Card>
            <Statistic title="Convocações" value={total} prefix={<ScheduleOutlined />} loading={loading} />
          </Card>
        </Col>
        <Col xs={12} sm={8}>
          <Card>
            <Statistic title="Pendentes" value={pendingCount} prefix={<ClockCircleOutlined />} loading={loading} />
          </Card>
        </Col>
        <Col xs={12} sm={8}>
          <Card>
            <Statistic title="Times" value={teamsCount} prefix={<TeamOutlined />} loading={loading} />
          </Card>
        </Col>
      </Row>

      <Card style={{ marginBottom: 16 }}>
        <Row gutter={[12, 12]} align="middle">
          <Col xs={24} md={8}>
            <RangePicker
              value={range}
              onChange={(v) => { setRange(v || [dayjs(), dayjs()]); setPage(1); }}
              format="DD/MM/YYYY"
              allowClear={false}
              style={{ width: '100%' }}
            />
          </Col>
          <Col xs={24} md={6}>
            <Input
              placeholder="Buscar time..."
              value={customerNameInput}
              onChange={(e) => setCustomerNameInput(e.target.value)}
              allowClear
            />
          </Col>
          <Col xs={12} md={4}>
            <Select
              placeholder="Tipo"
              value={callType}
              onChange={(v) => { setCallType(v); setPage(1); }}
              options={CALL_TYPE_OPTIONS.map((c) => ({ value: c.value, label: c.label }))}
              allowClear
              style={{ width: '100%' }}
            />
          </Col>
          <Col xs={12} md={4}>
            <Select
              placeholder="Status"
              value={status}
              onChange={(v) => { setStatus(v); setPage(1); }}
              options={STATUS_OPTIONS.map((s) => ({ value: s.value, label: s.label }))}
              allowClear
              style={{ width: '100%' }}
            />
          </Col>
          <Col xs={24} md={2} style={{ textAlign: 'right' }}>
            <Button icon={<ReloadOutlined />} onClick={() => load()} title="Atualizar" />
          </Col>
        </Row>
      </Card>

      <Table
        rowKey={(r) => `${r.game_id}-${r.call_type}`}
        loading={loading}
        dataSource={items}
        columns={columns}
        onRow={(record) => ({
          onClick: () => openDetail(record.game_id),
          style: { cursor: 'pointer' },
        })}
        pagination={{
          current: page,
          pageSize,
          total,
          showSizeChanger: true,
          pageSizeOptions: ['25', '50', '100', '200'],
          onChange: (p, ps) => { setPage(p); setPageSize(ps); },
        }}
        scroll={{ x: 'max-content' }}
      />

      <Modal
        title="Detalhes da Convocação"
        open={detailOpen}
        onCancel={() => setDetailOpen(false)}
        footer={[<Button key="close" onClick={() => setDetailOpen(false)}>Fechar</Button>]}
        width={620}
        destroyOnClose
      >
        {detailLoading || !detail ? (
          <div style={{ textAlign: 'center', padding: 40 }}><Spin /></div>
        ) : (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <Title level={4} style={{ margin: 0 }}>
                {detail.customer.game_name}
                <Text type="secondary" style={{ fontSize: 14, marginLeft: 8 }}>
                  {[
                    detail.customer.day_of_game,
                    detail.game.date_of_game ? dayjs(detail.game.date_of_game).format('DD/MM/YYYY') : null,
                    detail.customer.start_game,
                  ].filter(Boolean).join(' - ')}
                </Text>
              </Title>
              <Tag color={detail.game.status === 'ABERTO' ? 'blue' : 'default'}>
                {detail.game.status}
              </Tag>
            </div>

            <Descriptions size="small" column={2} bordered style={{ marginBottom: 16 }}>
              <Descriptions.Item label="Jogadores">{detail.customer.players ?? '-'}</Descriptions.Item>
              <Descriptions.Item label="Goleiros">{detail.customer.goalkeepers ?? '-'}</Descriptions.Item>
            </Descriptions>

            <Text strong>Status dos Jogadores:</Text>
            <Descriptions size="small" column={2} bordered style={{ marginTop: 8, marginBottom: 16 }}>
              <Descriptions.Item label="Vai jogar">
                <Tag color="green">{detail.player_status.vai_jogar}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Fora">
                <Tag color="red">{detail.player_status.fora}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Espera">
                <Tag color="gold">{detail.player_status.espera}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Não confirmado">
                <Tag>{detail.player_status.nao_confirmado}</Tag>
              </Descriptions.Item>
            </Descriptions>

            <Text strong>Convocações:</Text>
            <Descriptions size="small" column={1} bordered style={{ marginTop: 8 }}>
              {[
                { ordinal: '1ª', date: detail.game.date_early_call,  sent: detail.game.sent_early_call },
                { ordinal: '2ª', date: detail.game.date_first_call,  sent: detail.game.sent_first_call },
                { ordinal: '3ª', date: detail.game.date_second_call, sent: detail.game.sent_second_call },
                { ordinal: '4ª', date: detail.game.date_third_call,  sent: detail.game.sent_third_call },
              ].map((c) => (
                <Descriptions.Item key={c.ordinal} label={c.ordinal}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span>{c.date || '(não agendada)'}</span>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      {c.sent ? (
                        <>
                          <Text style={{ color: '#52C41A', fontWeight: 500 }}>Enviada</Text>
                          <CheckCircleTwoTone twoToneColor="#52C41A" />
                        </>
                      ) : (
                        <>
                          <Text style={{ color: '#F5222D', fontWeight: 500 }}>Não enviada</Text>
                          <CloseCircleTwoTone twoToneColor="#F5222D" />
                        </>
                      )}
                    </span>
                  </div>
                </Descriptions.Item>
              ))}
            </Descriptions>

            <div style={{ marginTop: 16, color: '#8c8c8c' }}>
              <Text type="secondary">Status atual: <strong>{detail.game.status}</strong></Text>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default Convocations;
